import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Container as LayoutContainer, PageHeader } from '../layout';
import { Button } from '../layout/buttons';
import { Row, Col } from 'react-bootstrap';
import styles from './Profile.module.css';
import { fetchClientById, updateClient } from '../../utils/clients';
import { setClient } from '../../utils/globalState';
import { getAccessToken as msalGetAccessToken, fetchGraphMe } from '../../utils/auth';
import { logger } from '../../utils/logger';
import CompanyManagement from './CompanyManagement';
import StakeholderManagement from './StakeholderManagement';

const Profile = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedDisplayName, setEditedDisplayName] = useState('');
  const [userCompany, setUserCompany] = useState(null);

  useEffect(() => {
    if (accounts.length > 0) {
      getProfileData();
    }
  }, [accounts]);

  const getAccessToken = async (scopes) => msalGetAccessToken({ instance, account: accounts[0], scopes });

  const getProfileData = async () => {
    setLoading(true);
    setError('');
    try {
      const accessToken = await getAccessToken(['User.Read']);
      const baseSelect = 'displayName,jobTitle,companyName,department,streetAddress,city,state,postalCode,country,userPrincipalName,id,mail,customSecurityAttributes';
      const data = await fetchGraphMe({ accessToken, select: baseSelect });
      setUserData(prev => ({ ...prev, ...data }));
      setEditedDisplayName(data.displayName || '');

      // Also fetch local client profile from our backend and merge (e.g., industry)
      try {
        if (data?.id) {
          const localClient = await fetchClientById({ clientId: data.id });
          localClient.userViewRole = data.customSecurityAttributes?.userViewRole;
          if (localClient) {
            logger.log('userData',  userData);
            setEditedDisplayName(localClient.displayName || data.displayName || '');
            setUserData(prev => 
              ({ ...prev, displayName: localClient.displayName }));
            setClient(localClient);
          }
        }
      } catch (e) {
        logger.warn('Local client fetch failed', e);
      }
    } catch (err) {
      logger.error('Error fetching profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyUpdate = (company) => {
    setUserCompany(company);
    // Update user data with company information
    setUserData(prev => ({
      ...prev,
      companyId: company.id,
      companyName: company.name
    }));
  };

  const handleEditProfile = async () => {
    setError('');
    try {
      // const accessToken = await getAccessToken(['User.ReadWrite.All']);

      // const patchResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
      //   method: 'PATCH',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${accessToken}`,
      //   },
      //   body: JSON.stringify({
      //     displayName: editedDisplayName,
      //     jobTitle: editedJobTitle,
      //     companyName: editedCompanyName,
      //   }),
      // });
      // if (!patchResponse.ok) {
      //   throw new Error(`Graph PATCH failed: ${patchResponse.status}`);
      // }

      // Update local client in our backend
      try {
        const clientId = userData?.id;
        if (clientId) {
          const localClient = await updateClient({
            clientId,
            body: {
              displayName: editedDisplayName,
            },
          });
          if (localClient) {
            setUserData(prev => ({
              ...prev,
              displayName: localClient.displayName ?? editedDisplayName,
            }));
            setClient(localClient);
          }
        }
      } catch (e) {
        // Ignore local update errors; UI already updated via Graph
      }

      setEditMode(false);
      await getProfileData();
    } catch (err) {
      logger.error('Error updating profile:', err);
      setError('Failed to update profile. You may not have permission.');
    }
  };

  return (
    <LayoutContainer fluid>
        <Row className="align-items-center">
            <Col>
                <PageHeader title="Your Profile" subtext="View your account information"/>
            </Col>
        </Row>
        <LayoutContainer className={styles.dataContainer}>
            {loading && <div>Loading profile...</div>}
            {error && <div style={{ color: 'var(--brand-as-red-600, #dc2626)', marginBottom: 12 }}>{error}</div>}
            {userData && (
              <div className={styles.profileGrid}>
                {/* Left: Profile summary card */}
                <div className={styles.dataContainerCard}>  
                  <div className={styles.profileHeader}>
                    <div className={styles.profileAvatar}>
                      {(editedDisplayName || userData.displayName || userData.mail || 'U').trim().split(/\s+/).map(p => p[0]).slice(0,2).join('').toUpperCase()}
                    </div>
                    <div className={styles.profileHeaderText}>
                      <div className={styles.profileName}>{editedDisplayName ||userData.displayName}</div>
                      <div className={styles.profileEmail}>{userData.mail || userData.userPrincipalName}</div>
                    </div>
                  </div>

                  <div className={styles.profileInfoGrid}>
                    <div className={styles.profileInfoLabel}>User Principal Name</div>
                    <div className={styles.profileInfoValue}>{userData.userPrincipalName || '—'}</div>

                    <div className={styles.profileInfoLabel}>ID</div>
                    <div className={styles.profileInfoValue}>{userData.id || '—'}</div>

                    <div className={styles.profileInfoLabel}>Is Admin</div>
                    <div className={styles.profileInfoValue}>
                      <input
                        type="checkbox"
                        checked={!!accounts[0]?.idTokenClaims?.roles?.includes('staging_administrator') || accounts[0]?.idTokenClaims?.roles?.includes('production_administrator')}
                        readOnly
                        disabled
                      />
                    </div>

                  </div>

                  <div className={styles.profileActions}>
                    {!editMode && (
                      <Button variant="outline-neutral" onClick={() => setEditMode(true)}>Edit Profile</Button>
                    )}
                  </div>
                </div>

                {/* Right: Edit panel card (shown only in edit mode) */}
                {editMode && (
                  <div className={styles.dataContainerCard2}>
                    <div className={styles.editCardTitle}>Edit Profile</div>
                    <div className={styles.editForm}>
                      <div className={styles.editFieldRow}>
                        <label>Display Name</label>
                        <input
                          type="text"
                          value={editedDisplayName}
                          onChange={(e) => setEditedDisplayName(e.target.value)}
                          className={styles.editInput}
                        />
                      </div>
                      {/*<div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                        <label>Department</label>
                        <input
                          type="text"
                          value={editedDepartment}
                          onChange={(e) => setEditedDepartment(e.target.value)}
                          style={{
                            border: '1px solid var(--brand-as-neutral-black-transparent-10)',
                            borderRadius: 8,
                            padding: '8px 10px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                        <label>Industry</label>
                        <input
                          type="text"
                          value={editedIndustry}
                          onChange={(e) => setEditedIndustry(e.target.value)}
                          style={{
                            border: '1px solid var(--brand-as-neutral-black-transparent-10)',
                            borderRadius: 8,
                            padding: '8px 10px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                        <label>Company Name</label>
                        <input
                          type="text"
                          value={editedCompanyName}
                          onChange={(e) => setEditedCompanyName(e.target.value)}
                          style={{
                            border: '1px solid var(--brand-as-neutral-black-transparent-10)',
                            borderRadius: 8,
                            padding: '8px 10px'
                          }}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                        <label>Department</label>
                        <input
                          type="text"
                          value={editedDepartment}
                          onChange={(e) => setEditedDepartment(e.target.value)}
                          style={{
                            border: '1px solid var(--brand-as-neutral-black-transparent-10)',
                            borderRadius: 8,
                            padding: '8px 10px'
                          }}
                        />
                      </div> */}
                      <div className={styles.editButtonRow}>
                        <Button variant="primary" onClick={handleEditProfile}>Save</Button>
                        <Button variant="outline-neutral" onClick={() => { 
                          setEditMode(false); 
                          setEditedDisplayName(userData.displayName || '');
                        }}>Cancel</Button>
                      </div>
                    </div>
                  </div>
                )}
                {!editMode && (
                  <div className={styles.dataContainerCard2}>
                    <div className={styles.profileBlurbTitle}>Complete your profile</div>
                    <div className={styles.profileBlurbText}>
                      Adding details like your job title, company, and industry helps August tailor canvass creation,
                      personalize prompts, and produce richer insights. A more complete profile leads to clearer
                      objectives and a more engaging experience for interview participants.
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Company and Stakeholder Management Section */}
            <div className={styles.managementSection}>
              <CompanyManagement userData={userData} onCompanyUpdate={handleCompanyUpdate} />
              {/* <StakeholderManagement company={userCompany} userData={userData} /> */}
            </div>
        </LayoutContainer>
    </LayoutContainer>
  );
};

export default Profile;
